import LocalizedTextField from '@components/Customization/LocalizedTextField';
import FieldSettings from '@components/Customization/PoiTypes/FieldSettings';
import RuleEditor, { type RuleCandidate } from '@components/Customization/PoiTypes/RuleEditor';
import { FieldTypeIcon } from '@components/Customization/fieldTypes';
import { chosenIds, withDefaultToggled, withoutChoice, withType } from '@lib/customization/fieldDraft';
import {
  followUpAnswers, readReports, readRequired, readVisibility, writeVisibility, type FollowUpAnswer, type ReportMode,
  type SimpleVisibility,
} from '@lib/customization/poiDesign';
import { type Container } from '@lib/customization/poiDraft';
import { isAlwaysAnswered } from '@lib/customization/poiRules';
import { useLocalization } from '@lib/localization';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, IconButton, MenuItem, Stack, Switch, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import {
  formInputTypes, hasText, newEntityId, ruleTargets, type FormInputType, type PoiField,
} from '@store/api/poiTypeEndpoints';
import { useState } from 'react';
import { AngleDownIcon, CodeBranchIcon, CopyIcon, PlusIcon, StarIcon, StarOIcon, TimesIcon, TrashIcon } from 'react-line-awesome';
import { TypeMenu, type MenuAnchor } from './CanvasMenus';
import ConditionBuilder from './ConditionBuilder';
import { addButtonStyles, LOGIC_COLOR, sectionTitleStyles, segmentedStyles } from './designer.styles';
import { answerLabel, ruleWords } from './describe';
import { type Languages } from './FieldHeading';

interface Props {
  field: PoiField;
  container: Container;
  groupNames: string[];
  /** Every field a condition could point at, this one included; it is left out here. */
  candidates: ReadonlyMap<string, RuleCandidate>;
  /** How many follow-ups each of this field's answers opens, keyed like `followUpAnswers`. */
  followUpCounts: ReadonlyMap<string, number>;
  languages: Languages;
  /** Replaces the field: for edits that rewrite it whole, like a type switch. */
  onChange: (field: PoiField) => void;
  /**
   * Merges into the field as it is in the draft, not as it was when this rendered: two
   * patches in one event (the rule editor sends a base flag and a rule) must both land.
   */
  onPatch: (changes: Partial<PoiField>) => void;
  onMove: (target: Container) => void;
  onAddFollowUp: (answer: FollowUpAnswer, type: FormInputType) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  disabled: boolean;
}

const Section: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({ title, action, children }) => (
  <Box sx={{ px: 2, pt: 2 }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25, minHeight: 24 }}>
      <Typography sx={sectionTitleStyles}>{title}</Typography>
      {action}
    </Stack>
    {children}
  </Box>
);

/** A switch on one line, no box around it; what it does, on hover. */
const SwitchLine: React.FC<{ label: string; hint: string; checked: boolean; disabled?: boolean; onChange: (checked: boolean) => void }> = ({
  label, hint, checked, disabled, onChange,
}) => (
  <Tooltip title={hint} placement="left">
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ minHeight: 34 }}>
      <Typography variant="body2" color={disabled ? 'text.disabled' : 'text.primary'}>{label}</Typography>
      <Switch size="small" checked={checked} disabled={disabled} onChange={(_event, value) => onChange(value)} />
    </Stack>
  </Tooltip>
);

/**
 * Everything about one field, in the order an author needs it: the question and its choices,
 * when it is asked, how its answer behaves, then the rest.
 */
const FieldInspector: React.FC<Props> = ({
  field, container, groupNames, candidates, followUpCounts, languages, onChange, onPatch: patch, onMove, onAddFollowUp,
  onDuplicate, onDelete, disabled,
}) => {
  const { t } = useLocalization();
  const { resolve } = languages;
  const [typeMenu, setTypeMenu] = useState<{ at: MenuAnchor; answer: FollowUpAnswer } | null>(null);

  const visibility = readVisibility(field);
  // Open on the rule editor when the rules are beyond the builder; the author can also ask for it.
  const [rulesOpen, setRulesOpen] = useState(visibility.mode === 'advanced');
  const advanced = rulesOpen || visibility.mode === 'advanced';

  const required = readRequired(field);
  const reports = readReports(field);
  const alwaysAnswered = isAlwaysAnswered(field.type);
  const isChoice = field.type === 'List' || field.type === 'MultiList';
  const others = [...candidates.values()].filter((candidate) => candidate.id !== field.id);
  // Previewing another language: that is the translation the cursor should land in.
  const focusLocale = languages.locale === languages.primary ? undefined : languages.locale;
  const answers = followUpAnswers(field);

  /**
   * Every edit the simple controls make goes through here, so the stored pair is written the
   * same way each time. A field that becomes conditional starts printing only when asked:
   * printing it always fills the report with a dash for every branch nobody took.
   */
  const writeSimple = (next: { visibility?: SimpleVisibility; reports?: ReportMode; required?: boolean }) => {
    if (visibility.mode === 'advanced' && !next.visibility) return;

    const nextVisibility = next.visibility ?? visibility as SimpleVisibility;
    const conditional = nextVisibility.mode === 'when';
    const nextReports = next.reports
      ?? (conditional && visibility.mode !== 'when' && reports === 'always' ? 'whenAsked'
        : !conditional && reports === 'whenAsked' ? 'always'
          : reports);

    onChange(writeVisibility(field, nextVisibility, nextReports, next.required ?? required));
  };

  /** "+" or how many follow-ups an answer already opens; a click adds one more. */
  const followUpButton = (answer: FollowUpAnswer) => {
    const count = followUpCounts.get(answer.key) ?? 0;

    return (
      <Tooltip title={t('Customization_Designer_AddFollowUpFor', [answerLabel(answer, resolve)])}>
        <span>
          <Button
            size="small"
            disabled={disabled}
            startIcon={<CodeBranchIcon />}
            onClick={(event) => setTypeMenu({ at: { element: event.currentTarget }, answer })}
            sx={{ ...addButtonStyles, minWidth: 48, px: 0.75, justifyContent: 'flex-start', color: count ? LOGIC_COLOR : 'text.disabled' }}
          >
            {count || '+'}
          </Button>
        </span>
      </Tooltip>
    );
  };

  return (
    <Box sx={{ pb: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, pt: 1.75 }}>
        <FieldTypeIcon type={field.type} size={28} />
        <TextField
          select size="small" disabled={disabled} value={field.type} sx={{ flex: 1 }}
          onChange={(event) => onChange(withType(field, event.target.value as FormInputType))}
        >
          {formInputTypes.map((type) => <MenuItem key={type} value={type}>{t(`Customization_FieldType_${type}`)}</MenuItem>)}
        </TextField>
        <Tooltip title={t('Customization_Duplicate')}><span><IconButton size="small" disabled={disabled} onClick={onDuplicate}><CopyIcon /></IconButton></span></Tooltip>
        <Tooltip title={t('Common_Delete')}><span><IconButton size="small" disabled={disabled} onClick={onDelete}><TrashIcon /></IconButton></span></Tooltip>
      </Stack>

      <Stack spacing={1.5} sx={{ px: 2, pt: 2 }}>
        {/* The primary language in the box, so it is always clear which element this is; the
            translations are behind the button beside it. */}
        <LocalizedTextField
          label={t('Customization_Designer_Question')}
          value={field.label}
          disabled={disabled}
          focusLocale={focusLocale}
          // A field just added has no question yet: that is the first thing to type.
          autoFocus={!hasText(field.label)}
          onChange={(label) => patch({ label })}
        />
        <TextField
          size="small" label={t('Customization_HelpText')} disabled={disabled} value={field.help ?? ''}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(event) => patch({ help: event.target.value || undefined })}
        />
      </Stack>

      {isChoice && (
        <Section title={t('Customization_Choices')}>
          <Stack spacing={1}>
            {(field.options ?? []).map((option, index) => (
              <Stack key={option.id ?? index} direction="row" spacing={0.25} alignItems="center">
                <Tooltip title={t('Customization_Designer_Default')}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={disabled}
                      onClick={() => option.id && patch(withDefaultToggled(field, option.id))}
                      sx={{ color: option.id && chosenIds(field).includes(option.id) ? 'warning.main' : 'text.disabled', fontSize: 15 }}
                    >
                      {option.id && chosenIds(field).includes(option.id) ? <StarIcon /> : <StarOIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <LocalizedTextField
                    label=""
                    placeholder={t('Customization_Designer_ChoiceN', [index + 1])}
                    value={option.label}
                    disabled={disabled}
                    focusLocale={focusLocale}
                    onChange={(label) => patch({
                      options: (field.options ?? []).map((current, position) => (position === index ? { ...option, label } : current)),
                    })}
                  />
                </Box>
                {answers.filter((answer) => answer.key === option.id).map(followUpButton)}
                <IconButton
                  size="small"
                  disabled={disabled || (field.options ?? []).length <= 1}
                  onClick={() => patch(withoutChoice(field, index))}
                  sx={{ color: 'text.disabled', fontSize: 14 }}
                >
                  <TimesIcon />
                </IconButton>
              </Stack>
            ))}
            <Box>
              <Button
                size="small" startIcon={<PlusIcon />} disabled={disabled} sx={addButtonStyles}
                onClick={() => patch({ options: [...(field.options ?? []), { id: newEntityId(), label: {} }] })}
              >
                {t('Customization_AddChoice')}
              </Button>
            </Box>
          </Stack>
        </Section>
      )}

      {/* Types without choices still have answers to branch on: Yes and No, a value, a text. */}
      {!isChoice && answers.length > 0 && (
        <Section title={t('Customization_Designer_FollowUps')}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            {answers.map((answer) => (
              <Stack key={answer.key} direction="row" spacing={0.5} alignItems="center">
                <Typography variant="body2" color="text.secondary">{answerLabel(answer, resolve)}</Typography>
                {followUpButton(answer)}
              </Stack>
            ))}
          </Stack>
        </Section>
      )}

      <Section
        title={t('Customization_Designer_WhenAsked')}
        action={!advanced && (
          <Button size="small" sx={{ ...addButtonStyles, fontSize: 12 }} onClick={() => setRulesOpen(true)}>{t('Customization_Designer_OpenAdvanced')}</Button>
        )}
      >
        {!advanced && (
          <ConditionBuilder
            visibility={visibility}
            candidates={others}
            resolve={resolve}
            disabled={disabled}
            onChange={(next) => writeSimple({ visibility: next })}
          />
        )}

        {advanced && (
          <Stack spacing={1.25}>
            {/* The rules in words first: what the author is about to change. */}
            {(field.rules ?? []).map((rule, index) => (
              <Typography key={rule.id ?? index} variant="body2" sx={{ color: LOGIC_COLOR }}>
                {ruleWords(rule, candidates, resolve)}
              </Typography>
            ))}
            <RuleEditor
              rules={field.rules ?? []}
              onChange={(rules) => patch({ rules: rules.length ? rules : undefined })}
              candidates={others}
              selfId={field.id}
              targets={alwaysAnswered ? ruleTargets.filter((target) => target !== 'required') : ruleTargets}
              baseVisible={field.visible !== false}
              onBaseVisibleChange={(visible) => patch({ visible })}
              disabled={disabled}
            />
            <SwitchLine
              label={t('Customization_Designer_VisibleBase')}
              hint={t('Customization_Designer_VisibleBaseHint')}
              checked={field.visible !== false}
              disabled={disabled}
              onChange={(visible) => patch({ visible })}
            />
            <Box>
              <Tooltip title={t('Customization_Designer_BackToSimpleHelp')}>
                <span>
                  <Button
                    size="small"
                    disabled={disabled}
                    sx={addButtonStyles}
                    onClick={() => {
                      setRulesOpen(false);

                      // Rules the builder can already show stay exactly as they are.
                      if (visibility.mode !== 'advanced') return;

                      // Otherwise keep the first rule's condition, usually what the author meant.
                      const first = field.rules?.[0];

                      writeSimple({
                        visibility: first
                          ? { mode: 'when', show: first.then.visible !== false, match: first.when.match, conditions: first.when.conditions, ruleId: first.id }
                          : { mode: field.visible === false ? 'never' : 'always' },
                        reports,
                      });
                    }}
                  >
                    {t('Customization_Designer_BackToSimple')}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Stack>
        )}
      </Section>

      <Section title={t('Customization_Designer_Answer')}>
        <SwitchLine
          label={visibility.mode === 'when' && visibility.show ? t('Customization_Designer_RequiredWhenAsked') : t('Customization_Required')}
          hint={alwaysAnswered ? t('Customization_RequiredNotApplicable') : t('Customization_Designer_RequiredHelp')}
          checked={required && !alwaysAnswered}
          disabled={disabled || alwaysAnswered}
          onChange={(checked) => (advanced ? patch({ required: checked }) : writeSimple({ required: checked }))}
        />
        <SwitchLine
          label={t('Customization_ReadOnly')} hint={t('Customization_ReadOnlyHelp')} checked={field.readOnly} disabled={disabled}
          onChange={(readOnly) => patch({ readOnly })}
        />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ minHeight: 40 }}>
          <Tooltip
            placement="left"
            title={{ always: t('Customization_Designer_ReportsHelpAlways'), whenAsked: t('Customization_Designer_ReportsHelpWhenAsked'), never: t('Customization_Designer_ReportsHelpNever') }[reports]}
          >
            <Typography variant="body2">{t('Customization_Designer_OnReports')}</Typography>
          </Tooltip>
          <ToggleButtonGroup
            exclusive size="small" disabled={disabled} value={reports} sx={segmentedStyles}
            onChange={(_event, next: ReportMode | null) => {
              if (!next) return;
              if (advanced) patch({ showOnReports: next !== 'never' });
              else writeSimple({ reports: next });
            }}
          >
            <ToggleButton value="always">{t('Customization_Designer_Always')}</ToggleButton>
            {/* Only meaningful when something decides whether the field is asked. */}
            {!advanced && visibility.mode === 'when' && <ToggleButton value="whenAsked">{t('Customization_Designer_WhenAskedShort')}</ToggleButton>}
            <ToggleButton value="never">{t('Customization_Designer_Never')}</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Section>

      <Accordion disableGutters elevation={0} sx={{ mt: 1, backgroundColor: 'transparent', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<AngleDownIcon />} sx={{ px: 2 }}>
          <Typography sx={sectionTitleStyles}>{t('Customization_Designer_MoreSettings')}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2 }}>
          <Stack spacing={2}>
            <TextField
              select size="small" label={t('Customization_Group')} disabled={disabled} value={String(container)}
              onChange={(event) => onMove(event.target.value === 'type' ? 'type' : Number(event.target.value))}
            >
              <MenuItem value="type">{t('Customization_NoGroup')}</MenuItem>
              {groupNames.map((name, index) => <MenuItem key={index} value={String(index)}>{name}</MenuItem>)}
            </TextField>
            <FieldSettings draft={field} patch={patch} disabled={disabled} />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <TypeMenu at={typeMenu?.at ?? null} onPick={(type) => typeMenu && onAddFollowUp(typeMenu.answer, type)} onClose={() => setTypeMenu(null)} />
    </Box>
  );
};

export default FieldInspector;
